import { memo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { ObjectStatus } from "@/db/schema"
import { completeTask, uncompleteTask, cancelTask } from "@/db/operations"
import { routeContainerStatus } from "@/features/project-header/projectStatusRequest"
import CheckmarkIcon from "@/shared/icons/totodoicons/stroke/checkmark.svg?react"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import {
  PickerItem,
  PickerHeader,
} from "@/shared/components/picker"

interface StatusSelectorProps {
  taskId: string
  currentStatus: ObjectStatus
  onClose: () => void
}

/**
 * Status selector — choose between Unfinished, Completed, and Canceled.
 * Opens as an inline panel in the detail view.
 */
export const StatusSelector = memo(function StatusSelector({
  taskId,
  currentStatus,
  onClose,
}: StatusSelectorProps) {
  const { t } = useTranslation()

  const handleUnfinished = useCallback(async () => {
    if (!(await routeContainerStatus(taskId, ObjectStatus.Unfinished))) await uncompleteTask(taskId)
    onClose()
  }, [taskId, onClose])

  const handleCompleted = useCallback(async () => {
    if (!(await routeContainerStatus(taskId, ObjectStatus.Completed))) await completeTask(taskId)
    onClose()
  }, [taskId, onClose])

  const handleCanceled = useCallback(async () => {
    if (!(await routeContainerStatus(taskId, ObjectStatus.Canceled))) await cancelTask(taskId)
    onClose()
  }, [taskId, onClose])

  const statuses = [
    {
      key: ObjectStatus.Unfinished,
      label: t("To Do"),
      icon: (
        <div className="flex size-4 items-center justify-center rounded-full border-2 border-muted-foreground/40" />
      ),
      onClick: handleUnfinished,
    },
    {
      key: ObjectStatus.Completed,
      label: t("Completed"),
      icon: (
        <div className="flex size-4 items-center justify-center rounded-full bg-primary">
          <CheckmarkIcon className="size-2.5 text-primary-foreground" />
        </div>
      ),
      onClick: handleCompleted,
    },
    {
      key: ObjectStatus.Canceled,
      label: t("Canceled"),
      icon: (
        <div className="flex size-4 items-center justify-center rounded-full bg-primary">
          <CrossIcon className="size-2.5 text-primary-foreground" />
        </div>
      ),
      onClick: handleCanceled,
    },
  ]

  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-border bg-card p-2 shadow-lg">
      <PickerHeader>{t("Status")}</PickerHeader>
      {statuses.map((s) => (
        <PickerItem
          key={s.key}
          selected={currentStatus === s.key}
          icon={s.icon}
          onClick={s.onClick}
        >
          {s.label}
        </PickerItem>
      ))}
    </div>
  )
})
